import { Fragment, useMemo, useState } from "react";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry";
import { match } from "ts-pattern";
import { useCabinetStore } from "../../../context/StoreContext";
import { getDimensions } from "../../../hooks/getDimensions";
import { useMaterialContext } from "../context/MaterialContext";
import { CORNER_SMOOTHING, getCornerRounding } from "../data";
import { BACKGROUND_THICKNESS } from "../data/Background";
import type { Horizontal as HorizontalType } from "../data/Horizontal";

interface Props {
	horizontal: HorizontalType;
}

export const FEET_HEIGHT = 0.005;
const HOLE_DIAMETER = 0.008;
const MAX_GAP_BETWEEN_FEET = 700;

export function Horizontal({ horizontal }: Props) {
	const { getMaterial } = useMaterialContext();
	const { state, api } = useCabinetStore();
	const cornerRounding: number = getCornerRounding(state, api);
	const [hover, setHover] = useState(false);

	const { geometry, dimensions } = useMemo(() => {
		const dimensions = getDimensions(horizontal);

		return {
			dimensions,
			geometry: new RoundedBoxGeometry(
				dimensions.width,
				dimensions.height,
				dimensions.depth,
				CORNER_SMOOTHING,
				cornerRounding,
			),
		};
	}, [horizontal, cornerRounding]);

	const material = getMaterial({
		variant: "horizontal",
		width: horizontal.width,
		height: horizontal.height,
		depth: horizontal.depth,
		hover,
	});

	// calculate the position of the feet holes based on actual machine code
	const feetHoles = useMemo(() => {
		if (horizontal.y !== 0 || state.hasFeet === false) {
			return [];
		}

		const isLeft = horizontal.x === 0;
		const isRight = horizontal.x + horizontal.width === state.width;

		const xAmount = Math.ceil(horizontal.width / MAX_GAP_BETWEEN_FEET) + 1;
		const xDistanceBetweenHoles = horizontal.width / (xAmount - 1);

		const thickness = state.thickness;
		const multiplier = isLeft || isRight ? 2 : 1.5;

		return Array.from({ length: xAmount }).flatMap((_, index) => {
			const x = match(index)
				.with(0, () => multiplier * thickness)
				.with(xAmount - 1, () => horizontal.width - multiplier * thickness)
				.otherwise(() => {
					// Check if calculatedX position is hitting underneath a vertical
					// If so, move 1 times thickness to the left
					const calculatedX = index * xDistanceBetweenHoles;

					const matchingVertical = Array.from(state.verticals.values()).find((v) => {
						return (
							v.x >= calculatedX - 2 * thickness && v.x + thickness <= calculatedX + 2 * thickness
						);
					});

					return matchingVertical ? matchingVertical.x - thickness : calculatedX;
				});

			return [
				{
					x: x - horizontal.width / 2,
					z: BACKGROUND_THICKNESS * 2 - horizontal.depth / 2,
				},
				{
					x: x - horizontal.width / 2,
					z: horizontal.depth - BACKGROUND_THICKNESS * 2 - horizontal.depth / 2,
				},
			];
		});
	}, [horizontal, state.hasFeet, state.thickness, state.width, state.verticals]);

	return (
		<group position={[dimensions.x, dimensions.y, dimensions.z]}>
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: no need for key event */}
			<mesh
				name={horizontal.id}
				onClick={(evt) => {
					evt.stopPropagation();
					console.info("Horizontal", horizontal);
				}}
				receiveShadow
				castShadow
				rotation={[0, 0, 0]}
				onPointerOver={(evt) => {
					setHover(true);
					evt.stopPropagation();
				}}
				onPointerOut={(evt) => {
					setHover(false);
					evt.stopPropagation();
				}}
				material={material}
				geometry={geometry}
			/>

			{feetHoles.map(({ x, z }, index) => (
				<Fragment key={index}>
					<mesh position={[x / 1000, 0.001, z / 1000]}>
						<cylinderGeometry args={[HOLE_DIAMETER, HOLE_DIAMETER, state.thickness / 1000]} />
						<meshStandardMaterial color={0x6b6b6b} />
					</mesh>

					<mesh
						position={[x / 1000, (FEET_HEIGHT / 2 + state.thickness / 1000 / 2) * -1, z / 1000]}
					>
						<cylinderGeometry args={[HOLE_DIAMETER * 2, HOLE_DIAMETER * 2, FEET_HEIGHT]} />
						<meshStandardMaterial color={0x000000} />
					</mesh>
				</Fragment>
			))}
		</group>
	);
}
